import axios from 'axios';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, USE_BEARER } from '../../constants';
import { setAuthorizedEvent } from '../../App';
import { BASE_URL } from './axiosInstances';

export const createAxiosInstance = (baseURL) => {
  const axiosRequest = axios.create({ baseURL });
  const axiosRaw = axios.create({ baseURL: BASE_URL });

  let isRefreshing = false;
  let refreshSubscribers = [];

  function onRefreshed(token) {
    refreshSubscribers.forEach((callback) => callback(token));
    refreshSubscribers = [];
  }

  function addRefreshSubscriber(callback) {
    refreshSubscribers.push(callback);
  }

  axiosRequest.interceptors.request.use(async (req) => {
    if (!req._auth) return req;
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (USE_BEARER && token) {
      req.headers.Authorization = `Bearer ${token}`;
    }
    return req;
  });

  axiosRequest.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const authTokens = {
        access: localStorage.getItem(ACCESS_TOKEN_KEY),
        refresh: localStorage.getItem(REFRESH_TOKEN_KEY),
      };

      if (error.response?.status === 401 && !originalRequest._retry && originalRequest._auth && authTokens.refresh) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            addRefreshSubscriber((token) => {
              if (token) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(axiosRequest(originalRequest));
              } else {
                reject(error);
              }
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshResponse = await axiosRaw.post('auth/refresh', { refresh_token: authTokens.refresh });
          const { access_token, refresh_token } = refreshResponse.data;
          localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
          localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
          setAuthorizedEvent(true);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          onRefreshed(access_token);
          return axiosRequest(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem(ACCESS_TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          setAuthorizedEvent(false);
          onRefreshed(null);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return axiosRequest;
};
